import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import ListBoxSelectorWithSearch from '@product-framework/components/ListBoxSelectorWithSearch';
import UnPaginatedList from '@product-framework/components/UnPaginatedList';
import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope/ServiceProviderScopeContext';

function ProductLinesSelector({
  onChange = () => undefined,
  selectedIndustry = {},
  permissions,
}) {
  const leftNameKey = 'productLineName';
  const leftValueKey = 'id';
  const rightValueKey = 'id';
  const limit = 10;

  const { formatMessage } = useLocalization();
  const { skillsConfig } = useServiceProviderScope();
  const config = skillsConfig ?? {};
  const productLineMessages = config?.ui?.messages?.productLinesSelector;

  const rightTitle = formatMessage({
    id: 'EXCLUDED_FROM_SERVICE',
    defaultMessage: 'Excluded from service',
  });

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, post } = useDataProvider(provider);

  const snackbar = useSnackbar();

  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(permissions?.productLineId, '');

  const getLeftItems = async (page, searchParams) => {
    // If no industry is selected, return empty array
    if (!selectedIndustry?.value) {
      return [];
    }
    let searchFilter = '';
    if (searchParams) {
      searchFilter = `&productLineName=${searchParams}`;
    }
    const response = await get(
      `${config?.api?.productLinesSelector?.listBoxLeft}?industryId=${
        selectedIndustry.value
      }&page=${page}&limit=${limit}${searchFilter}`,
    );

    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
          status: item.status,
        })) || [];
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = async page => {
    // If no industry is selected, return empty array
    if (!selectedIndustry?.value) {
      return [];
    }
    const response = await get(
      `${config?.api?.productLinesSelector?.listBoxRight}?industryId=${
        selectedIndustry.value
      }&page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item,
          status: item.status,
        })) || [];
      onChange(resultsData);
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const onItemsAdded = async items => {
    if (Array.isArray(items)) {
      const response = await post(
        `${config?.api?.productLinesSelector?.listBoxAdd}`,
        {
          productLineIds: items,
          industryId: selectedIndustry?.value,
        },
      );
      if (!response.ok) {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      } else {
        snackbar.showMessage(formatMessage(productLineMessages?.saveSuccess));
      }
    }
  };

  const onItemsRemoved = async items => {
    if (Array.isArray(items)) {
      const productLinesToRemove = items.map(item => item[rightValueKey]);
      const response = await post(
        `${config?.api?.productLinesSelector?.listBoxRemove}`,
        {
          excludedProductLineIds: productLinesToRemove,
        },
      );
      if (!response.ok) {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      } else {
        snackbar.showMessage(formatMessage(productLineMessages?.removeSuccess));
      }
    }
  };

  if (readOnly) {
    return (
      <UnPaginatedList
        parentId={selectedIndustry?.value || null}
        title={rightTitle}
        getItems={getRightItems}
      />
    );
  }

  return (
    <ListBoxSelectorWithSearch
      height="465px"
      getLeftItems={getLeftItems}
      getRightItems={getRightItems}
      parentId={selectedIndustry?.value || null}
      onChange={onChange}
      onItemsAdded={onItemsAdded}
      onItemsRemoved={onItemsRemoved}
      leftTitle={`${selectedIndustry?.label} Product Lines` || ''}
      rightTitle={rightTitle}
      searchPlaceholder={formatMessage({
        id: 'SEARCH_PRODUCT_LINES',
        defaultMessage: 'Search Product Lines',
      })}
    />
  );
}

export default ProductLinesSelector;
